import { Component, EventEmitter, forwardRef, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Subject } from 'rxjs/internal/Subject';
import { InfoService } from '@app/core/info/info.service';
import { SigningHistory, SigningHistoryRequest } from '@app/certificates/history/history';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatTableHelper } from '@app/shared/helpers/table-filter.helper';
import { environment } from '@env/environment';
import { IStatusIcon } from '@app/shared/interfaces/common-interfaces';
import { iconCodes } from '@app/shared/cqes-icon/cqes-icon.component';
import { HistoryService } from '../history.service';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import { SigningService } from '@app/certificates/signing/signing.service';
import { DatePipe } from '@angular/common';
import { ICertificateData } from '@app/certificates/manage/manage';
import { Store } from '@ngrx/store';
import { selectorCertificate } from '@app/certificates/manage/manage.reducer';
import { getCertificateFullName } from '@app/shared/helpers/certificate-name-helper';
import { ActionDownload } from '@app/certificates/signing/download.action';
import { IUserData } from '@app/profile/main/details';
import { SessionStorageService, selectorAuth } from '@app/core';
import { BusinessService } from '@app/business/business.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { HistoryDetailsComponent } from '@app/business/history-details/history-details.component';

export enum DocumentStatus {
  ERROR = 'ERROR',
  IN_PROGRESS = 'IN_PROGRESS',
  SIGNING = 'SIGNING',
  SIGNED = 'SIGNED',
  RECEIVED = 'RECEIVED',
  REJECTED = 'REJECTED',
  ARCHIVED = 'ARCHIVED',
  REMOVED = 'DELETED',
  EXPIRED = 'EXPIRED',
  SENT = 'SENT',
  DELIVERED = 'DELIVERED',
  VIEWED = 'VIEWED',
  FAILED = 'FAILED',
}

export enum SortableClolumsNames {
  TEXT_TO_SHOW = 'displayedText',
  DATE = 'createdDate',
  SIGN_DATE = 'signedDate',
  DOCUMENT_COUNT = 'documentCount',
  SENDER_BG = 'senderBg',
  SENDER_EN = 'senderEn'
}

export enum OrderHistorySortColumns {
  ORDER_DATE = 'order_date',
  ORDER_CODE = 'order_code',
  PLAN_NAME = 'plan_id',
  CERTIFICATE_SERIAL_NUMBER = 'serial_number',
  ORDER_STATUS = 'status',
  PAYMENT_DATE = 'payment_date',
  PLAN_SIGNING_COUNT = 'plan_signing_count',
  PRICE = 'price'
}

export const DocumentStatusMap = new Map<string, IStatusIcon>();
DocumentStatusMap.set(DocumentStatus.ERROR, {
  icon: iconCodes.error,
  colorClass: 'text-danger',
  tooltip: 'historyTexts.documentStatusError',
  key: DocumentStatus.ERROR,
  text: 'error'
} as IStatusIcon);
DocumentStatusMap.set(DocumentStatus.IN_PROGRESS, {
  icon: iconCodes.info,
  colorClass: 'text-warning',
  tooltip: 'historyTexts.documentStatusSigning',
  key: DocumentStatus.IN_PROGRESS,
  text: 'in progress'
} as IStatusIcon);
DocumentStatusMap.set(DocumentStatus.SIGNING, {
  icon: iconCodes.info,
  colorClass: 'text-warning',
  tooltip: 'historyTexts.documentStatusSigning',
  key: DocumentStatus.SIGNING,
  text: 'in progress'
} as IStatusIcon);
DocumentStatusMap.set(DocumentStatus.SIGNED, {
  icon: iconCodes.done,
  colorClass: 'text-success',
  tooltip: 'historyTexts.documentStatusSigned',
  key: DocumentStatus.SIGNED,
  text: 'successfully signed'
} as IStatusIcon);
DocumentStatusMap.set(DocumentStatus.REJECTED, {
  icon: iconCodes.blocked,
  colorClass: 'mat-primary',
  tooltip: 'historyTexts.documentStatusRejected',
  key: DocumentStatus.REJECTED,
  text: 'rejected by owner'
} as IStatusIcon);
DocumentStatusMap.set(DocumentStatus.REMOVED, {
  icon: iconCodes.delete,
  colorClass: 'text-danger',
  tooltip: 'historyTexts.documentStatusDeleted',
  key: DocumentStatus.REMOVED,
  text: 'removed'
} as IStatusIcon);
DocumentStatusMap.set(DocumentStatus.EXPIRED, {
  icon: iconCodes.hourglass_empty,
  colorClass: 'mat-primary',
  tooltip: 'historyTexts.documentStatusExpired',
  key: DocumentStatus.EXPIRED,
  text: 'expired'
} as IStatusIcon);

// document statuses - received and arhived are same visuals as Signed documents
DocumentStatusMap.set(DocumentStatus.RECEIVED, {
  icon: iconCodes.done,
  colorClass: 'text-success',
  tooltip: 'historyTexts.documentStatusSigned',
  key: DocumentStatus.RECEIVED,
  text: 'successfully signed'
} as IStatusIcon);

DocumentStatusMap.set(DocumentStatus.ARCHIVED, {
  icon: iconCodes.done,
  colorClass: 'text-success',
  tooltip: 'historyTexts.documentStatusSigned',
  key: DocumentStatus.SIGNED,
  text: 'successfully signed'
} as IStatusIcon);

DocumentStatusMap.set(DocumentStatus.SENT, {
  icon: iconCodes.done,
  colorClass: 'text-success',
  tooltip: 'historyTexts.documentStatusSentPackage',
  key: DocumentStatus.SIGNED,
  text: 'sent'
} as IStatusIcon);

DocumentStatusMap.set(DocumentStatus.DELIVERED, {
  icon: iconCodes.done_all,
  colorClass: 'text-success',
  tooltip: 'historyTexts.documentStatusDelivered',
  key: DocumentStatus.SIGNED,
  text: 'delivered'
} as IStatusIcon);

DocumentStatusMap.set(DocumentStatus.VIEWED, {
  icon: iconCodes.visibility,
  colorClass: 'text-success',
  tooltip: 'historyTexts.documentStatusViewed',
  key: DocumentStatus.SIGNED,
  text: 'viewed'
} as IStatusIcon);

DocumentStatusMap.set(DocumentStatus.FAILED, {
  icon: iconCodes.error,
  colorClass: 'text-danger',
  tooltip: 'historyTexts.documentStatusError',
  key: DocumentStatus.FAILED,
  text: 'failed'
} as IStatusIcon);




@Component({
  selector: 'cloud-signing-history',
  templateUrl: './signing-history.component.html',
  styleUrls: ['./signing-history.component.scss']
})

export class SigningHistoryComponent implements OnInit, OnDestroy {

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(forwardRef(() => MatSort)) sort: MatSort;
  @Output() isDataLoaded: EventEmitter<boolean> = new EventEmitter<boolean>();

  unsubscribe$: Subject<void> = new Subject<void>();
  signingHistoryDataSource: MatTableDataSource<SigningHistory> = new MatTableDataSource<SigningHistory>();

  displayedColumns = [];

  statusLablesDocument = [
    'ALL',
    'ERROR',
    'IN_PROGRESS',
    'SIGNED',
    // 'RECEIVED',
    'REJECTED',
    // 'ARCHIVED',
    'DELETED',
    'EXPIRED'
  ];

  statusLablesMultiple = [
    'ALL',
    'SIGNING',
    'ERROR',
    'SIGNED',
    'REJECTED',
    'DELETED',
    'ARCHIVED',
    'EXPIRED',
    // 'SENT', 
    'DELIVERED',
    'VIEWED'
  ];

  readonly dateFormat = environment.dateFormat;
  readonly timeFormat = environment.timeFormat;
  certificateNumbers: string[];
  displayAllCertsOption = false;
  certificates: ICertificateData[];
  fromDate: string;
  toDate: string;
  minEndDateValue: string;
  maxStartDateValue: string;
  documentNumber: any;
  loaded: boolean;
  signedfileName: any;
  resetCertificate: string;
  displayOptions: SigningHistoryRequest;
  userData: IUserData;
  downloadIds = [];
  selectValue;
  selectMultipleValue;

  constructor(
    public info: InfoService,
    private historyService: HistoryService,
    private signingService: SigningService,
    public tableHelper: MatTableHelper,
    private translateService: TranslateService,
    private businessService: BusinessService,
    private sessionStorageService: SessionStorageService,
    private translate: TranslateService,
    private store: Store<any>,
    public dialog: MatDialog,
  ) {
    this.store
      .select(selectorCertificate)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(certs => this.certificates = certs);
  }

  ngOnInit() {
    this.setDisplayColumns();
    this.loaded = false;
    this.initDisplayOptions();

    this.certificateNumbers = [];
    this.certificates.forEach(cert => {
      this.certificateNumbers.push(cert.serialNo);
    });
    this.getUserInfo();
    this.loadSigningHistory();
  }

  mockedResponseObjects = [
    // Login
    {
      createdDate: 1727675049605,
      receivedSignedDate: 1727675076877,
      contentTypeId: 10,
      displayedText: "Потвърдете вход в система: Клиентски портал local",
      status: "SIGNED",
      signType: "SIGNATURE",
      receivedDate: null,
      fileName: null,
      parcelUsageId: -1,
      usageIdDownloadRequested: 0,
      senderBg: "B-Trust Mobile",
      senderEn: "B-Trust Mobile",
      documentCount: 1,
    },
    // Document
    {
      createdDate: 1727675049605,
      receivedSignedDate: 1727675076877,
      displayedText: "Имате документ очакващ подпис: Заявление за ново участие UPF-participation-application-41697732.pdf",
      certId: 18149,
      status: "SIGNED",
      signType: "SIGNATURE",
      fileName: "management-request-41698283.txt",
      parcelUsageId: 172140,
      contentTypeId: 0,
      usageIdDownloadRequested: 0,
      senderBg: "agentsportal.b-trust.bg",
      senderEn: "agentsportal.b-trust.bg",
      documentCount: 1,
    },
    //Package
    {
      createdDate: 1727675049605,
      receivedSignedDate: 1727675076877,
      status: "IN_PROGRESS",
      senderBg: 'Иван Иванов',
      senderEn: 'Ivan Ivanov',
      parcelUsageId: 1368,
      displayedText: "Описание пратка test  222222",
      documentCount: 3,
      contentTypeId: 230,
    },
    //Registered mail
    {
      createdDate: 1727675049605,
      receivedSignedDate: 1727675076877,
      date: 1727272756369,
      status: "SENT",
      displayedText: "RegisteredMail",
      senderBg: 'Иван Иванов',
      senderEn: 'Ivan Ivanov',
      parcelUsageId: 1368,
      documentCount: 4,
      contentTypeId: 240,
    },
    {
      createdDate: 1727675049605,
      receivedSignedDate: 1727675076877,
      date: 1727272756369,
      status: "DELIVERED",
      displayedText: "RegisteredMail",
      senderBg: 'Иван Иванов',
      senderEn: 'Ivan Ivanov',
      parcelUsageId: 1400,
      documentCount: 4,
      contentTypeId: 240,
    },
    {
      createdDate: 1727675049605,
      receivedSignedDate: 1727675076877,
      date: 1727272756369,
      status: "VIEWED",
      displayedText: "RegisteredMail",
      senderBg: 'Иван Иванов',
      senderEn: 'Ivan Ivanov',
      parcelUsageId: 1368,
      documentCount: 4,
      contentTypeId: 240,
    }
  ]



  setDisplayColumns() {
    this.displayedColumns = [
      'status',
      'date',
      'signedDate',
      'objectType',
      'objectName',
      'senderName',
      'documentsCount',
      'actions'
    ];
  }

  getUserInfo() {
    this.store
      .select(selectorAuth)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        if (res.userData) { this.userData = res.userData; }
      });
  }


  getDocumentStatusText(value) {
    switch (value) {
      case 'ALL':
        return this.translateService.instant('historyTexts.all');
      case 'ERROR':
        return this.translateService.instant('historyTexts.documentStatusError');
      case 'IN_PROGRESS':
        return this.translateService.instant('historyTexts.documentStatusSigning');
      case 'SIGNED':
        return this.translateService.instant('historyTexts.documentStatusSigned');
      // case 'RECEIVED':
      //   return this.translateService.instant('historyTexts.documentStatusReceived');
      case 'REJECTED':
        return this.translateService.instant('historyTexts.documentStatusRejected');
      // case 'ARCHIVED':
      //   return this.translateService.instant('historyTexts.documentStatusArchived');
      case 'DELETED':
        return this.translateService.instant('historyTexts.documentStatusDeleted');
      case 'EXPIRED':
        return this.translateService.instant('historyTexts.documentStatusExpired');
      default:
        return this.translateService.instant('historyTexts.documentStatusSigned');
    }
  }

  getPackageStatusText(value) { 
    switch (value) {
      case 'ALL':
        return this.translateService.instant('historyTexts.all');
      case 'SIGNING':
        return this.translateService.instant('historyTexts.documentStatusSigning');
      case 'ERROR':
        return this.translateService.instant('historyTexts.documentStatusError');
      case 'SIGNED':
        return this.translateService.instant('historyTexts.documentStatusSigned');
      case 'REJECTED':
        return this.translateService.instant('historyTexts.documentStatusRejected');
      case 'ARCHIVED':
        return this.translateService.instant('historyTexts.documentStatusArchived');
      case 'DELETED':
        return this.translateService.instant('historyTexts.documentStatusDeleted');
      case 'EXPIRED':
        return this.translateService.instant('historyTexts.documentStatusExpired');
      case 'SENT':
        return this.translateService.instant('historyTexts.documentStatusSentPackage');
      case 'DELIVERED':
        return this.translateService.instant('historyTexts.documentStatusDelivered');
      case 'VIEWED':
        return this.translateService.instant('historyTexts.documentStatusViewed');
      default:
        return this.translateService.instant('historyTexts.documentStatusSigned');
    }
  }

  isStatusDisabled() {
   
    if (!this.displayOptions.type || this.displayOptions.type === 'LOGIN' || this.displayOptions.type === 'REGISTERED_DELIVERY' || this.displayOptions.type === 'BATCH_SIGNING') {
      return true;
    } else {
      return false;
    }
  }

  isMultipleStatusDisabled() {
    if (!this.displayOptions.type || this.displayOptions.type === 'LOGIN' || this.displayOptions.type === 'DOCUMENT') {
      return true;
    } else {
      return false;
    }
  }



  loadSigningHistory() {
    this.historyService.getSigningHistory(this.displayOptions, false).subscribe(res => {
      if (res.data) {
        this.setDataSource(res.data.content);
        this.documentNumber = res.data.size;
        this.setPaginator();
      }
    });
  }


  /**
   * Set the paginator and sort after the view init since this component will
   * be able to query its view for the initialized paginator and sort.
   */
  setDataSource(signingHistory: SigningHistory[]) {
    this.loaded = true;
    this.isDataLoaded.emit(false);
    let dataSource = [];
    if (signingHistory) {
     let filteredSigningHistory = signingHistory.filter((s) => !(s.status == 'SENT' && s.contentTypeId== 240)); // filter out sent registered mail entries

      dataSource = filteredSigningHistory.map(a => Object.assign({
        date: a.createdDate,
        signedDate: a.receivedSignedDate,
        name: a.displayedText ? a.displayedText : a.filename,
        certId: a.certId,
        certSerialNumber: a.certSerialNumber,
        relyingPartyId: a.relyingPartyId,
        relyingPartyName: a.relyingPartyName,
        status: a.status,
        statusIcon: DocumentStatusMap.get(a.status),
        parcelUsageId: a.parcelUsageId,
        documentCount: a.documentCount,
        usageIdDownloadRequested: a.usageIdDownloadRequested,
        id: a.id,
        contentTypeId: a.contentTypeId,
        senderBg: a.senderBg,
        senderEn: a.senderEn,
        contentFormat:a.contentFormat,
        archiveStatus:a.archiveStatus
      }));
    }
    if (this.certificateNumbers.length > 1) {
      this.displayAllCertsOption = true;
    }
    this.signingHistoryDataSource = new MatTableDataSource(dataSource);
  }

  getSenderName(o) {
    return this.translate.currentLang === 'bg' ? o.senderBg : o.senderEn
  }

  private setPaginator() {
    this.paginator.pageIndex = this.displayOptions.offset;
    this.paginator.pageSize = this.displayOptions.limit;
    this.paginator.length = this.documentNumber;
  }

  hasData() {
    return (this.signingHistoryDataSource.data.length !== 0);
  }

  isOverLimit() {
    return (this.signingHistoryDataSource.data.length >= 500);
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  searchDocument() {
    this.displayOptions.offset = 0;
    this.loadSigningHistory();
  }

  setOrderStatusSingle(status) {
    this.selectMultipleValue = '';
    this.displayOptions.status = status;
    this.displayOptions.offset = 0;
    if (this.displayOptions.status && this.displayOptions.status === 'ALL') {
      delete this.displayOptions.status;
    }
    // this.loadSigningHistory();
  }

  setOrderStatusMultiple(status) {
    this.selectValue = '';
    this.displayOptions.status = status;
    this.displayOptions.offset = 0;
    if (this.displayOptions.status && this.displayOptions.status === 'ALL') {
      delete this.displayOptions.status;
    }
    // this.loadSigningHistory();
  }


  setSignOptions(type) {
    this.displayOptions.offset = 0;
    this.displayOptions.type = type;
    if(this.displayOptions.status){
      this.selectValue='';
      this.selectMultipleValue='';
      delete this.displayOptions.status;
    }
    //this.loadSigningHistory();
  }

  resetLoginOptions() {
    this.displayOptions.isLogin = false;
    this.displayOptions.type = 'ALL';
    this.displayOptions.offset = 0;
    //this.loadSigningHistory();
  }

  setRequestDates() {
    const pip: DatePipe = new DatePipe('bg-BG');

    if (this.fromDate) {
      this.minEndDateValue = this.fromDate;
      const startDate = new Date(this.fromDate).setHours(0, 0, 0, 0);
      this.displayOptions.fromCreatedDate = encodeURIComponent(pip.transform(startDate, 'yyyy-MM-dd\'T\'HH:mm:ss.SSSUTC'));
    }

    if (this.toDate) {
      this.maxStartDateValue = this.toDate;
      const endDate = new Date(this.toDate).setHours(23, 59, 59, 999);
      this.displayOptions.toCreatedDate = encodeURIComponent(pip.transform(endDate, 'yyyy-MM-dd\'T\'HH:mm:ss.SSSUTC'));
    }
    this.displayOptions.offset = 0;
   // this.loadSigningHistory();
  }

  onPaginateChange(paginator) {
    this.displayOptions.limit = paginator.pageSize;
    this.displayOptions.offset += (paginator.pageIndex - paginator.previousPageIndex);
    this.loadSigningHistory();
  }

  sortByHeader(sorter) {
    const columnName = SigningHistoryComponent.getColumnName(sorter.id, this.translate.currentLang);
    if (!sorter._arrowDirection) {
      sorter._arrowDirection = 'desc';
    }
    this.displayOptions.orderByFilter = columnName;
    this.displayOptions.order = sorter._arrowDirection.toUpperCase();
    this.loadSigningHistory();
  }

  static getColumnName(name, lang?) {
    switch (name) {
      case 'date':
        return SortableClolumsNames.DATE;
      case 'signedDate':
        return SortableClolumsNames.SIGN_DATE;
      case 'objectName':
        return SortableClolumsNames.TEXT_TO_SHOW;
      case 'senderName':
        return lang === 'bg' ? SortableClolumsNames.SENDER_BG : SortableClolumsNames.SENDER_EN ;
        case 'documentsCount':
        return SortableClolumsNames.DOCUMENT_COUNT;
      default:
        return SortableClolumsNames.DATE;
    }
  }

  resetFilters() {
    this.fromDate = '';
    this.maxStartDateValue = '';
    this.toDate = '';
    this.minEndDateValue = '';
    this.resetCertificate = '';
    this.selectValue='';
    this.selectMultipleValue='';
    this.initDisplayOptions();
  }

  private initDisplayOptions() {
    this.displayOptions = {};
    this.displayOptions.offset = 0;
    this.displayOptions.limit = 10;
  }

  clearText() {
    this.displayOptions.textToShow = '';
    delete this.displayOptions.textToShow;
    this.displayOptions.offset = 0;
    //this.loadSigningHistory();
  }

  clearFromDate() {
    this.fromDate = '';
    delete this.fromDate;
    this.displayOptions.fromCreatedDate = '';
    delete this.displayOptions.fromCreatedDate;
    this.displayOptions.offset = 0;
  }

  clearToDate() {
    this.toDate = '';
    delete this.toDate;
    this.displayOptions.toCreatedDate = '';
    delete this.displayOptions.toCreatedDate;
    this.displayOptions.offset = 0;
  }

  clearTextSender() {
    this.displayOptions.sender = '';
    delete this.displayOptions.sender;
    this.displayOptions.offset = 0;
    //this.loadSigningHistory();
  }

  onDownloadCert(cert: SigningHistory) {
    this.downloadFile(cert);
  }

  onDownloadRequestedCert(cert: SigningHistory) {
    this.downloadRequestedFile(cert);
  }

  downloadFile(cert: SigningHistory) {
    this.signingService.getFile(cert.parcelUsageId.toString())
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (result) => {
          if (result.status !== 200) {
            this.info.error(result.message);
          } else {
            this.signedfileName = result.headers.get('Content-Disposition');
            let fileName = this.signedfileName.substring(this.signedfileName.indexOf('=') + 2, (this.signedfileName.length - 1));
            if (SigningHistoryComponent.isIE()) {
              fileName = decodeURIComponent(fileName);
              const nav = (window.navigator as any)
              if (nav.msSaveOrOpenBlob) {
                nav.msSaveOrOpenBlob(result.body, fileName);
              }
            } else {
              const url = window.URL.createObjectURL(result.body);
              const link = document.createElement('a');
              link.href = url;
              link.download = decodeURI(fileName);
              document.body.appendChild(link);
              link.click();
            }
          }
          this.store.dispatch(new ActionDownload());
        }
      );
  }

  downloadRequestedFile(cert: SigningHistory) {
    this.signingService.getRequestedFile(cert.usageIdDownloadRequested.toString())
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (result) => {
          if (result.status !== 200) {
            this.info.error(result.message);
          } else {
            this.signedfileName = result.headers.get('Content-Disposition');
            let fileName = this.signedfileName.substring(this.signedfileName.indexOf('=') + 2, (this.signedfileName.length - 1));
            if (SigningHistoryComponent.isIE()) {
              fileName = decodeURIComponent(fileName);
              const nav = (window.navigator as any)
              if (nav.msSaveOrOpenBlob) {
                nav.msSaveOrOpenBlob(result.body, fileName);
              }
            } else {
              const url = window.URL.createObjectURL(result.body);
              const link = document.createElement('a');
              link.href = url;
              link.download = decodeURI(fileName);
              document.body.appendChild(link);
              link.click();
            }
          }
        }
      );
  }

  onDownloadReport(cert: SigningHistory, reportType: string) {
    this.signingService.getReport(cert.parcelUsageId.toString(), reportType)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (result) => {
          if (result.status !== 200) {
            this.info.error(result.message);
          } else {
            this.signedfileName = result.headers.get('Content-Disposition');
            let fileName = this.signedfileName.substring(this.signedfileName.indexOf('=') + 2, (this.signedfileName.length - 1));
            if (SigningHistoryComponent.isIE()) {
              fileName = decodeURIComponent(fileName);
              const nav = (window.navigator as any)
              if (nav.msSaveOrOpenBlob) {
                nav.msSaveOrOpenBlob(result.body, fileName);
              }
            } else {
              const url = window.URL.createObjectURL(result.body);
              const link = document.createElement('a');
              link.href = url;
              link.download = decodeURI(fileName);
              document.body.appendChild(link);
              link.click();
            }
          }
        }
      );
  }

  onDownloadFile(fileId: any) {
    this.signingService.getRequestedFile(fileId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (result) => {
          if (result.status !== 200) {
            this.info.error(result.message);
          } else {
            this.signedfileName = result.headers.get('Content-Disposition');
            let fileName = this.signedfileName.substring(this.signedfileName.indexOf('=') + 2, (this.signedfileName.length - 1));
            if (this.isIE()) {
              fileName = decodeURIComponent(fileName);
              const nav = (window.navigator as any)
              if (nav.msSaveOrOpenBlob) {
                nav.msSaveOrOpenBlob(result.body, fileName);
              }
            } else {
              const url = window.URL.createObjectURL(result.body);
              const link = document.createElement('a');
              link.href = url;
              link.download = decodeURI(fileName);
              document.body.appendChild(link);
              link.click();
            }
          }
        }
      );
  }

  isIE() {
    const match = navigator.userAgent.search(/(?:Edge|MSIE|Trident\/.*; rv:)/);
    let isIE = false;
    if (match !== -1) {
      isIE = true;
    }
    return isIE;
  }

  checkSingleDocument(o) {
    return o.contentTypeId == 0;
  }

  checkMultipleDocuments(o) {
    if(o.contentTypeId === 230){
      return false;
    }
    if(o.contentTypeId === 240){
      return o.status === 'DELIVERED' || o.status === 'VIEWED'
    }
    return false;
  }
  checkMultipleDocumentsDetails(o){
    return o.contentTypeId == 230 || o.contentTypeId == 240;
  }
  checkActions(o) {
    if(o.contentTypeId === 10){
      return false;
    }
    
    if(o.contentFormat === 'DIGEST'){
      return false;
    }

    if(o.contentTypeId == 0){
      if(o.parcelUsageId === -1){
        return false;
      }
    };
    return true;
  }

  checkForDownload(row: SigningHistory): boolean {
    if(row.parcelUsageId == -1){
      return false;
    }
    if(row.contentTypeId === 230){
      return false; 
    }

    if(row.contentTypeId == 0){
      return row.status !== 'DELETED' && row.status !== 'ERROR';
    };
    return false;
  }

  checkRequestedForDownload(cert: SigningHistory): boolean {
    return cert.usageIdDownloadRequested > 0 && cert.usageIdDownloadRequested !== undefined;
  }

  openDetailsDialog(o: any) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      requestedId: o.parcelUsageId,
      status: o.status,
      typeId: o.contentTypeId,
      personalAcc:true,
      archiveStatus:o.archiveStatus
    };

    dialogConfig.panelClass = 'panel-class-history';

    const dialogRef = this.dialog.open(HistoryDetailsComponent, dialogConfig);
    const sub = dialogRef.componentInstance.close.subscribe(() => {
      this.dialog.closeAll();
      this.loadSigningHistory();
    });
  }

  onDownloadMultiple(o) {
    this.businessService.getDownloadDocIds(o.parcelUsageId).subscribe(res => {
      if (res) {
        this.downloadIds = res.data.documentsForDownload;
        this.downloadIds.forEach(id => {
          this.onDownloadFile(id);
        })
      }
    })
  }

  getDownloadHeader(typeId) {
    if (typeId == 230) {
      return this.translateService.instant('historyTexts.downloadPackage');
    }
    if (typeId == 240) {
      return this.translateService.instant('historyTexts.downloadRegisteredMail');
    }
  }

  getTypeHeader(typeId) {
    if (typeId == 230) {
      return this.translateService.instant('historyTexts.packageDocuments');
    }
    if (typeId == 240) {
      return this.translateService.instant('historyTexts.registeredMail');
    }
    if (typeId == 10) {
      return this.translateService.instant('historyTexts.login');
    }
    if (typeId == 0) {
      return this.translateService.instant('historyTexts.document');
    }
  }

  isArhived(cert: SigningHistory): boolean {
    return cert.status === DocumentStatus.ARCHIVED;
  }

  static isIE() {
    const match = navigator.userAgent.search(/(?:Edge|MSIE|Trident\/.*; rv:)/);
    let isIE = false;
    if (match !== -1) {
      isIE = true;
    }
    return isIE;
  }

  getCertificateName(cert: ICertificateData) {
    return getCertificateFullName(cert);
  }
}
